import { ContactOverview } from '@aibees/elephant/components/contact-overview';
import { CurrentCompany } from '@aibees/elephant/components/current-company';
import { EntityInfoPanel } from '@aibees/elephant/components/entity-info-panel';
import { OverviewPersonality } from '@aibees/elephant/components/overview-personality';
import { PeopleAnalysisBlurOverlay } from '@aibees/elephant/components/people-analysis-blur-overlay';
import { PeopleContactData } from '@aibees/elephant/components/people-contact-data';
import { PersonUpdateAlertMessage } from '@aibees/elephant/components/person-update-alert-message';
import {
  SeaAllFilterField,
  TagAction,
} from '@aibees/elephant/components/see-all-popup';
import {
  CompanyReadDataProvider,
  usePersonReadDataContext,
} from '@aibees/elephant/context';
import { RecordManagement } from '@aibees/elephant/feature/search-drawer/components/record-management';
import { SearchBar } from '@aibees/elephant/feature/search-drawer/components/search-bar';
import { Technologies } from '@aibees/elephant/feature/search-drawer/feature/insight/components/technologies';
import { useSearchPersonStore } from '@aibees/elephant/feature/search-table-people/store';
import { useGetPersonUpdatesData } from '@aibees/elephant/hooks/people/use-get-person-updates-data';
import { usePersonCompanyInfo } from '@aibees/elephant/hooks/people/use-person-company-info';
import { SearchType } from '@aibees/elephant/types';
import { classnames } from '@aibees/shared/utils';
import { Box, Divider, Stack } from '@mui/material';
import { memo } from 'react';
import { ContactDetails } from '../../components/contact-details';
import { ParentFeature } from '../../components/contact-details/context';
import { ContactDetailsProvider } from '../../components/contact-details/context/provider';
import { ContactHeadline } from '../../components/contact-headline';
import { ContactOverview as PeopleOverview } from '../../components/contact-overview';
import * as Styled from './styles';
import * as SX from './sx';
import type { OverviewProps as Props } from './types';

export const Overview = memo(({ className, handleChangeTab }: Props) => {
  const { api } = usePersonReadDataContext();
  const { hasAnyUpdates } = useGetPersonUpdatesData(api?.data);
  const { id } = useSearchPersonStore();
  const { id: personCompanyId } = usePersonCompanyInfo(api?.data);

  return (
    <CompanyReadDataProvider id={personCompanyId}>
      <Styled.Wrapper className={classnames('Overview', className)}>
        {hasAnyUpdates && (
          <PersonUpdateAlertMessage onClickUpdate={handleChangeTab} />
        )}
        <Box p={1.5}>
          <ContactHeadline />
        </Box>
        <Divider />{' '}
        {api?.data?.profile?.summary && (
          <>
            <Box p={1.5}>
              <PeopleOverview />
            </Box>
            <Divider />
          </>
        )}
        <Stack sx={SX.content}>
          <PeopleContactData key={id} />
          <RecordManagement id={id} type={SearchType.People} />
          <ContactOverview />
          <EntityInfoPanel
            icon="dashboard-outline"
            title="Overview Personality"
          >
            {api?.data?.link?.linkedin && (
              <PeopleAnalysisBlurOverlay isSidebar>
                <OverviewPersonality isSidebar />
              </PeopleAnalysisBlurOverlay>
            )}
          </EntityInfoPanel>
          <Stack gap={1}>
            <SearchBar />
            <ContactDetailsProvider
              parentFeature={ParentFeature.PERSON_DETAILS_SIDEBAR}
              key={api?.data?.id}
            >
              <ContactDetails />
            </ContactDetailsProvider>
            <CurrentCompany
              isSidebar
              companyName={
                api?.data?.company?.summary?.name ??
                api?.data?.positionGroups?.[0]?.company?.name ??
                undefined
              }
            />
            <Technologies
              config={{
                actions: [
                  TagAction.INCLUDE_IN_EXISTING_SEARCH,
                  TagAction.INCLUDE_IN_NEW_SEARCH,
                  TagAction.EXCLUDE_IN_EXISTING_SEARCH,
                  TagAction.EXCLUDE_IN_NEW_SEARCH,
                ],
                field: SeaAllFilterField.technologies,
                placeholder: 'Search Technology Tags',
              }}
            />
          </Stack>
        </Stack>
      </Styled.Wrapper>
    </CompanyReadDataProvider>
  );
});

Overview.displayName = 'Overview';
